/**
 * Product components: <product-info>, <variant-picker>, <media-gallery>.
 * Variant changes are handled fully client-side from the variant JSON, so the
 * same code works on the product page and in the Home "featured product" section.
 * Depends on theme.js (window.themeUtils).
 */

(() => {
  if (customElements.get('product-info')) return;

  const { formatMoney, announce } = window.themeUtils || {};
  const strings = window.theme?.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Media gallery ----------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slidesList = this.querySelector('[data-slides]');
      this.dots = this.querySelector('[data-dots]');
      this.lightbox = this.querySelector('[data-lightbox]');

      this.querySelectorAll('[data-thumb]').forEach((thumb) =>
        thumb.addEventListener('click', () => this.setActive(thumb.dataset.thumb))
      );
      this.slidesList?.addEventListener(
        'scroll',
        window.themeUtils.debounce(() => this.syncFromScroll(), 80),
        { passive: true }
      );

      // Zoom
      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom]');
        if (zoom) this.openLightbox(zoom);
        if (event.target.closest('[data-lightbox-close]') || event.target === this.lightbox) this.closeLightbox();
      });

      this.buildDots();
      this.markCurrent(this.querySelector('.product-gallery__slide.is-active'));
    }

    get visibleSlides() {
      return [...this.querySelectorAll('.product-gallery__slide:not([hidden])')];
    }

    /** Show only media for the chosen swatch value (plus shared media). */
    filter(group) {
      const slides = [...this.querySelectorAll('[data-slides] > [data-group]')];
      const hasGroup = group && slides.some((slide) => slide.dataset.group === group);
      // Hidden slides were never lazy-loaded; fetch the incoming ones right away.
      slides.forEach((slide) => {
        if (slide.dataset.group === group) slide.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      });
      this.querySelectorAll('[data-group]').forEach((el) => {
        el.hidden = hasGroup && el.dataset.group !== '' && el.dataset.group !== group;
      });
      this.dataset.selectedGroup = group;
      this.buildDots();
      const first = this.visibleSlides.find((s) => s.dataset.group === group) || this.visibleSlides[0];
      if (first) this.setActive(first.dataset.mediaId, { smooth: false });
    }

    setActive(mediaId, { smooth = true } = {}) {
      const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide || slide.hidden) return;
      this.querySelectorAll('.product-gallery__slide').forEach((el) => el.classList.toggle('is-active', el === slide));
      this.markCurrent(slide);

      // On mobile the slides are a horizontal scroller.
      const list = this.slidesList;
      if (list && list.scrollWidth > list.clientWidth + 2) {
        list.scrollTo({ left: slide.offsetLeft - list.offsetLeft, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
      }
    }

    markCurrent(slide) {
      if (!slide) return;
      const id = slide.dataset.mediaId;
      this.querySelectorAll('[data-thumb]').forEach((el) => el.setAttribute('aria-current', el.dataset.thumb === id));
      this.dots?.querySelectorAll('button').forEach((el) => el.setAttribute('aria-current', el.dataset.target === id));
    }

    syncFromScroll() {
      const list = this.slidesList;
      if (!list || list.scrollWidth <= list.clientWidth + 2) return;
      const index = Math.round(list.scrollLeft / list.clientWidth);
      const slide = this.visibleSlides[index];
      if (!slide) return;
      this.querySelectorAll('.product-gallery__slide').forEach((el) => el.classList.toggle('is-active', el === slide));
      this.markCurrent(slide);
    }

    buildDots() {
      if (!this.dots) return;
      const slides = this.visibleSlides;
      this.dots.replaceChildren();
      this.dots.hidden = slides.length < 2;
      slides.forEach((slide, i) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-gallery__dot';
        button.dataset.target = slide.dataset.mediaId;
        button.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
        button.addEventListener('click', () => this.setActive(slide.dataset.mediaId));
        li.append(button);
        this.dots.append(li);
      });
    }

    openLightbox(trigger) {
      if (!this.lightbox) return;
      const source = trigger.querySelector('img');
      const image = document.createElement('img');
      image.className = 'lightbox__image';
      image.src = trigger.dataset.zoom;
      image.alt = source?.alt || '';
      if (source) {
        image.width = source.width;
        image.height = source.height;
      }
      this.lightbox.querySelector('[data-lightbox-frame]').replaceChildren(image);
      this.lastTrigger = trigger;
      this.lightbox.showModal();
      document.body.classList.add('is-locked');
    }

    closeLightbox() {
      if (!this.lightbox?.open) return;
      this.lightbox.close();
      document.body.classList.remove('is-locked');
      this.lastTrigger?.focus();
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* Variant picker: only reads the selection and reports it ---------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', (event) => {
        const legend = event.target.closest('fieldset')?.querySelector('[data-selected-value]');
        if (legend) legend.textContent = event.target.value;
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: this.selectedVariant, picker: this } }));
      });
    }

    get selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    }

    get selectedVariant() {
      const selected = this.selectedOptions;
      return this.variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
    }

    get swatchGroup() {
      const position = Number(this.dataset.swatchPosition);
      if (!position) return '';
      const fieldset = this.querySelectorAll('fieldset')[position - 1];
      return fieldset?.querySelector('input:checked')?.dataset.valueHandle || '';
    }

    /** Mark values that can't be combined with the current selection. */
    updateAvailability() {
      const selected = this.selectedOptions;
      this.querySelectorAll('fieldset').forEach((fieldset, index) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              variant.options.every((value, i) => i === index || value === selected[i])
          );
          const label = input.nextElementSibling;
          label?.classList.toggle('swatch--unavailable', label.classList.contains('swatch') && !available);
          label?.classList.toggle('pill--unavailable', label.classList.contains('pill') && !available);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Product info: glue between picker, gallery, price and buttons ----------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.gallery = this.querySelector('media-gallery');
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail));
      this.initStickyBar();
    }

    onVariantChange({ variant, picker }) {
      picker.updateAvailability();
      this.gallery?.filter(picker.swatchGroup);

      if (variant?.featured_media && this.gallery) {
        this.gallery.setActive(variant.featured_media.id, { smooth: false });
      }

      this.updateButtons(variant);
      if (!variant) return;

      this.updatePrice(variant);
      this.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      const sticky = this.querySelector('[data-sticky-variant]');
      if (sticky) sticky.textContent = `${variant.title} · `;
      announce?.(`${variant.title}, ${formatMoney(variant.price)}`);
    }

    updatePrice(variant) {
      const container = this.querySelector('[data-price]');
      if (container) {
        const onSale = variant.compare_at_price > variant.price;
        const price = container.querySelector('.price');
        price?.classList.toggle('price--on-sale', onSale);
        price?.classList.toggle('price--sold-out', !variant.available);
        const current = container.querySelector('.price__current');
        if (current) current.innerHTML = formatMoney(variant.price);
        let compare = container.querySelector('.price__compare');
        if (onSale) {
          if (!compare) {
            compare = document.createElement('s');
            compare.className = 'price__compare';
            price?.append(compare);
          }
          compare.innerHTML = formatMoney(variant.compare_at_price);
        } else {
          compare?.remove();
        }
      }
      const stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.innerHTML = formatMoney(variant.price);
    }

    updateButtons(variant) {
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('[data-label]');
        const available = Boolean(variant?.available);
        button.disabled = !available;
        if (!label) return;
        if (!variant) label.textContent = strings.unavailable;
        else if (!available) label.textContent = strings.soldOut;
        else if (label.hasAttribute('data-short-label')) label.textContent = strings.addToCart;
        else label.textContent = (strings.addToCartWithPrice || strings.addToCart).replace('[price]', formatMoney(variant.price).replace(/<[^>]*>/g, ''));
      });
    }

    initStickyBar() {
      const bar = this.querySelector('[data-sticky-atc]');
      const mainButton = this.querySelector('.product-form [data-add-button]');
      if (!bar || !mainButton) return;
      const barButton = bar.querySelector('button');
      let visible = null;
      let ticking = false;

      // Show once the main button has scrolled up out of view. A scroll check (rather than
      // IntersectionObserver) also covers jumps past the button, e.g. restored scroll position.
      const update = () => {
        ticking = false;
        const show = mainButton.getBoundingClientRect().bottom < 0;
        if (show === visible) return;
        visible = show;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        barButton.tabIndex = show ? 0 : -1;
      };
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();
    }
  }
  customElements.define('product-info', ProductInfo);
})();
